/**
 * Charter eligibility toggle (admin)
 *
 * Self-contained widget that shows a driver's current charter dispatch
 * eligibility status (per `driver_details.charter_eligible` /
 * `charter_eligibility_note`) and lets an admin flip it via the
 * `admin_set_charter_eligible` RPC.
 *
 * Embedded in the Driver Verification doc-review sheet so when an
 * admin reviews license / contract carriage proof they can also
 * approve or revoke charter dispatch in one place.
 */

import { useCallback, useEffect, useState } from 'react';
import { Bus, ShieldCheck, ShieldX } from 'lucide-react';
import { toast } from 'sonner';

import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface CharterEligibility {
  charter_eligible: boolean;
  charter_eligible_at: string | null;
  charter_eligibility_note: string | null;
  charter_mode: 'off' | 'alongside' | 'charter_only';
}

export default function CharterEligibilityToggle({ driverId }: { driverId: string }) {
  const [data, setData] = useState<CharterEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data: row, error } = await supabase
        .from('driver_details')
        .select('charter_eligible, charter_eligible_at, charter_eligibility_note, charter_mode')
        .eq('id', driverId)
        .maybeSingle();
      if (error) throw error;
      if (row) setData(row as CharterEligibility);
    } catch (err: any) {
      console.warn('[charter-toggle] fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const flip = useCallback(
    async (eligible: boolean) => {
      const note = eligible
        ? window.prompt(
            'Optional note (e.g. license number reviewed, expiry date). Leave blank to skip:',
            data?.charter_eligibility_note ?? '',
          )
        : window.prompt(
            'Optional reason for revoking charter eligibility. Leave blank to skip:',
            '',
          );
      // null = user clicked Cancel — abort.
      if (note === null) return;
      setUpdating(true);
      try {
        const { data: out, error } = await supabase.rpc('admin_set_charter_eligible', {
          p_driver_id: driverId,
          p_eligible: eligible,
          p_note: note.trim() || null,
        });
        if (error) throw error;
        if ((out as any)?.ok === false) throw new Error((out as any).error);
        toast.success(eligible ? 'Charter dispatch enabled.' : 'Charter dispatch revoked.');
        await refresh();
      } catch (err: any) {
        toast.error(`Could not update: ${err?.message ?? 'unknown'}`);
      } finally {
        setUpdating(false);
      }
    },
    [driverId, data?.charter_eligibility_note, refresh],
  );

  if (loading || !data) {
    return (
      <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-3 text-xs text-gray-500">
        Loading charter status…
      </div>
    );
  }

  const eligible = data.charter_eligible;

  return (
    <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Bus className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-semibold text-white uppercase tracking-wider">
            Charter dispatch
          </span>
        </div>
        <Badge
          className={
            eligible
              ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-[10px]'
              : 'bg-gray-500/10 text-gray-400 border-gray-600/30 text-[10px]'
          }
        >
          {eligible ? 'eligible' : 'not eligible'}
        </Badge>
      </div>

      <p className="text-[11px] text-gray-400 leading-relaxed mb-2">
        {eligible
          ? `Driver can receive private charter requests. Current charter mode: ${data.charter_mode.replace(/_/g, ' ')}.`
          : 'Driver is not currently approved for private charter dispatch. Approve only after reviewing valid contract carriage / charter license proof.'}
      </p>

      {data.charter_eligibility_note && (
        <p className="text-[11px] text-gray-500 italic mb-2">
          Note: {data.charter_eligibility_note}
        </p>
      )}

      <div className="flex gap-2">
        {eligible ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => flip(false)}
            disabled={updating}
            className="h-7 px-2 text-xs border-rose-700/50 text-rose-300 hover:bg-rose-900/30"
          >
            <ShieldX className="w-3 h-3 mr-1" />
            Revoke
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => flip(true)}
            disabled={updating}
            className="h-7 px-2 text-xs border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/30"
          >
            <ShieldCheck className="w-3 h-3 mr-1" />
            Approve charter
          </Button>
        )}
      </div>
    </div>
  );
}
